/**
 * CompareStage — the scoring grid, the weighted ranking and the proposal
 * summaries. Lifted from the retired BriefWorkspace tab so the one-page
 * decision surface renders it as stage 3.
 */
import { StarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { rankOptions } from '../../../services/brief/briefService';
import type { Brief, BriefCriteria, BriefOptionWithProposal } from '../../../types/brief';

// ── Score cell ────────────────────────────────────────────────────────────────

function ScoreCell({
  score,
  onScore,
  disabled,
}: {
  score: number | null;
  onScore: (n: number) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          disabled={disabled}
          onClick={() => onScore(n)}
          className={`w-5 h-5 rounded transition-colors ${disabled ? 'cursor-default' : 'hover:scale-110'} ${
            score !== null && n <= score ? 'text-[var(--mustard)]' : 'text-[var(--text-dim)] hover:text-[var(--mustard)]'
          }`}
        >
          <StarIcon className="w-full h-full" fill={score !== null && n <= score ? 'currentColor' : 'none'} />
        </button>
      ))}
      {score !== null && <span className="text-xs text-[var(--text-ter)] ml-1">{score}</span>}
    </div>
  );
}

// ── Assumption conflict detector ──────────────────────────────────────────────

function detectConflicts(options: BriefOptionWithProposal[]): string[] {
  const conflicts: string[] = [];
  const optionsWithProposals = options.filter((o) => o.proposal?.assumptions?.length);
  if (optionsWithProposals.length < 2) return conflicts;

  const CONSTRAINT_KEYWORDS = [
    'budget', 'headcount', 'hire', 'team', 'timeline', 'quarter', 'year',
    'partner', 'api', 'integration', 'market', 'customer', 'revenue',
  ];

  const seen = new Map<string, { optionName: string; assumption: string }[]>();
  for (const opt of optionsWithProposals) {
    for (const assumption of opt.proposal!.assumptions) {
      const lower = assumption.toLowerCase();
      for (const kw of CONSTRAINT_KEYWORDS) {
        if (lower.includes(kw)) {
          const existing = seen.get(kw) ?? [];
          existing.push({ optionName: opt.name, assumption });
          seen.set(kw, existing);
        }
      }
    }
  }

  for (const [kw, entries] of seen) {
    if (entries.length >= 2) {
      const names = [...new Set(entries.map((e) => e.optionName))];
      if (names.length >= 2) {
        conflicts.push(`"${kw}" dependency: ${names.join(' and ')} both make assumptions about ${kw}.`);
      }
    }
  }
  return conflicts.slice(0, 5);
}

// ── Compare Panel ─────────────────────────────────────────────────────────────

export function CompareStage({
  brief,
  criteria,
  options,
  onScoreChange,
}: {
  brief: Brief;
  criteria: BriefCriteria[];
  options: BriefOptionWithProposal[];
  onScoreChange: (optionId: string, criteriaId: string, score: number) => void;
}) {
  const isLocked = brief.status === 'decided' || brief.status === 'deferred' || brief.status === 'archived';
  const ranked = rankOptions(options, criteria);
  const conflicts = detectConflicts(options);

  const getScore = (optionId: string, criteriaId: string): number | null => {
    const opt = options.find((o) => o.id === optionId);
    const score = opt?.scores.find((s) => s.criteria_id === criteriaId);
    return score?.score ?? null;
  };

  return (
    <div className="space-y-6">
      {/* Assumption conflicts */}
      {conflicts.length > 0 && (
        <div className="border border-[var(--mustard)]/30 bg-[var(--mustard)]/10 p-4">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[var(--mustard)] mb-1">Shared dependencies detected</p>
              <ul className="space-y-1">
                {conflicts.map((c, i) => (
                  <li key={i} className="text-xs text-[var(--mustard)]/80">{c}</li>
                ))}
              </ul>
              <p className="text-xs text-[var(--mustard)] mt-1">
                Verify that these proposals are truly independent before selecting more than one.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ranking summary */}
      {ranked.some((o) => o.weightedScore !== null) && (
        <div>
          <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-2">Ranking by weighted score</p>
          <div className="flex gap-4 flex-wrap">
            {ranked
              .filter((o) => o.rank !== null)
              .map((o) => (
                <div key={o.id} className="flex items-center gap-1.5 text-sm">
                  <span className="w-5 h-5 rounded-full bg-[var(--surface)] text-[var(--text-sec)] text-xs flex items-center justify-center font-medium flex-shrink-0">
                    {o.rank}
                  </span>
                  <span className="text-[var(--text-pri)]">{o.name}</span>
                  <span className="text-[var(--text-ter)] text-xs">({o.weightedScore?.toFixed(1)})</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Scoring matrix */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left text-xs text-[var(--text-ter)] font-medium pb-3 pr-4 uppercase tracking-wide w-32">
                Criteria
              </th>
              {options.map((o) => (
                <th key={o.id} className="text-left pb-3 px-3 min-w-36">
                  <div className="font-medium text-[var(--text-pri)] truncate">{o.name}</div>
                  {o.owner_name && <div className="text-xs text-[var(--text-ter)] font-normal">{o.owner_name}</div>}
                  {o.proposal === null && <div className="text-xs text-[var(--mustard)] font-normal">No proposal yet</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {criteria.map((c) => (
              <tr key={c.id} className="border-t border-[var(--rule)]">
                <td className="py-3 pr-4 align-top">
                  <div className="font-medium text-[var(--text-sec)]">{c.name}</div>
                  {c.description && <div className="text-xs text-[var(--text-ter)] mt-0.5">{c.description}</div>}
                  <div className="text-xs text-[var(--text-dim)] mt-0.5">Weight: {c.weight}</div>
                </td>
                {options.map((o) => (
                  <td key={o.id} className="py-3 px-3 align-top">
                    <ScoreCell
                      score={getScore(o.id, c.id)}
                      onScore={(n) => onScoreChange(o.id, c.id, n)}
                      disabled={isLocked}
                    />
                  </td>
                ))}
              </tr>
            ))}

            {/* Weighted total row */}
            <tr className="border-t-2 border-[var(--rule-bright)]">
              <td className="py-3 pr-4 font-medium text-[var(--text-sec)] text-sm">Weighted total</td>
              {ranked.map((o) => (
                <td key={o.id} className="py-3 px-3">
                  {o.weightedScore !== null ? (
                    <span className="font-semibold text-[var(--text-pri)]">{o.weightedScore.toFixed(1)}</span>
                  ) : (
                    <span className="text-[var(--text-dim)] text-xs">—</span>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Proposal summaries */}
      <div>
        <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-3">Submitted proposals</p>
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(options.length, 3)}, 1fr)` }}>
          {options.map((o, idx) => (
            <div key={o.id} className="border border-[var(--rule)] p-3 bg-[var(--surface)] text-xs space-y-2">
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 rounded-full bg-[var(--bg)] text-[var(--text-ter)] text-xs flex items-center justify-center font-medium flex-shrink-0">
                  {String.fromCharCode(65 + idx)}
                </span>
                <span className="font-medium text-[var(--text-pri)] truncate">{o.name}</span>
              </div>
              {o.proposal ? (
                <>
                  {o.proposal.rationale && <p className="text-[var(--text-sec)] line-clamp-3">{o.proposal.rationale}</p>}
                  {o.proposal.investment_summary && (
                    <p className="text-[var(--text-ter)]">
                      <span className="font-medium text-[var(--text-sec)]">Investment:</span> {o.proposal.investment_summary}
                    </p>
                  )}
                  {o.proposal.assumptions?.length > 0 && (
                    <div>
                      <p className="font-medium text-[var(--text-sec)] mb-1">Assumptions:</p>
                      <ul className="space-y-0.5">
                        {o.proposal.assumptions.map((a, i) => (
                          <li key={i} className="text-[var(--text-ter)]">· {a}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {o.proposal.top_risk && (
                    <p className="text-[var(--text-ter)]">
                      <span className="font-medium text-[var(--text-sec)]">Risk:</span> {o.proposal.top_risk}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-[var(--text-dim)] italic">Proposal not yet submitted.</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
